import { useEffect, useMemo, useState } from 'react';
import { readSeenTemplateTips, writeSeenTemplateTips } from './publishCoachmarkState';
import type { TemplateId } from './types';
import './PublishCoachmark.css';

/**
 * One-shot starter tip for the publish composer. The first time a
 * kid lands on a given template, a small card appears anchored to
 * the part most worth understanding — the widgets panel, the quiz
 * target, the sound stage. After they tap "Got it", the tip is
 * sticky-dismissed for that template. Other templates surface their
 * own tips on first open.
 *
 * The card sits in `position: fixed` and points at the anchor with a
 * small triangle. We re-measure on resize / scroll so the bubble
 * follows the layout.
 */

export interface PublishCoachmarkProps {
  /** Active template — drives which tip surfaces (or none). */
  template: TemplateId;
}

interface TipDef {
  template: TemplateId;
  /** CSS selector for the element the bubble points at. Falls back
   *  to a floating bottom card if the anchor isn't on the page. */
  selector: string;
  title: string;
  body: string;
}

const TIPS: ReadonlyArray<TipDef> = [
  {
    template: 'sticker-cam',
    selector: '.bl-composer__widgets',
    title: 'Make it yours with widgets',
    body: 'Each toggle adds a piece to your live page — a banner, a score, a QR code, share buttons. Mix what fits the story you want to tell.',
  },
  {
    template: 'class-quiz',
    selector: '.bl-pub-classquiz__target',
    title: 'The target rotates',
    body: 'Class Quiz picks a class at random and asks viewers to mimic it. Match the target → score goes up. Switch classes → streak resets.',
  },
  {
    template: 'sound-reactor',
    selector: '.bl-pub-soundreactor__stage',
    title: 'Make a noise the model knows',
    body: 'The pulse changes colour when your model hears something it learned. Try the wave layout for a meter look — same magic, different vibe.',
  },
];

export function PublishCoachmark({ template }: PublishCoachmarkProps): JSX.Element | null {
  const [seen, setSeen] = useState<Set<TemplateId>>(() => readSeenTemplateTips());
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const tip = useMemo(() => {
    if (seen.has(template)) return null;
    return TIPS.find((t) => t.template === template) ?? null;
  }, [seen, template]);

  useEffect(() => {
    if (!tip) {
      setAnchorRect(null);
      return;
    }
    const measure = (): void => {
      const target = document.querySelector(tip.selector);
      if (!target) {
        setAnchorRect(null);
        return;
      }
      setAnchorRect(target.getBoundingClientRect());
    };
    measure();
    const onResize = (): void => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    // The preview pane mounts a touch later than the composer, so
    // re-measure on the next frame too.
    const handle = window.setTimeout(measure, 120);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      window.clearTimeout(handle);
    };
  }, [tip]);

  if (!tip) return null;

  function dismiss(): void {
    if (!tip) return;
    setSeen((prev) => {
      const next = new Set(prev);
      next.add(tip.template);
      writeSeenTemplateTips(next);
      return next;
    });
  }

  const BUBBLE_MAX_W = 360;
  const ARROW_PAD = 18;

  let style: React.CSSProperties;
  let arrowX: number | null = null;
  if (anchorRect && typeof window !== 'undefined') {
    const bubbleW = Math.min(BUBBLE_MAX_W, window.innerWidth - 16);
    const left = Math.max(
      8,
      Math.min(
        window.innerWidth - bubbleW - 8,
        anchorRect.left + anchorRect.width / 2 - bubbleW / 2,
      ),
    );
    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    arrowX = Math.max(ARROW_PAD, Math.min(bubbleW - ARROW_PAD, anchorCenter - left));
    style = {
      position: 'fixed',
      top: Math.max(8, anchorRect.bottom + 12),
      left,
      ['--bl-pub-coach-arrow-x' as string]: `${arrowX}px`,
    };
  } else {
    style = { position: 'fixed', right: 16, bottom: 16 };
  }

  return (
    <div
      role="dialog"
      aria-label={tip.title}
      aria-describedby={`bl-pub-coach-body-${tip.template}`}
      className="bl-pub-coach"
      data-anchored={arrowX !== null ? 'true' : undefined}
      style={style}
    >
      <p className="bl-pub-coach__title">{tip.title}</p>
      <p className="bl-pub-coach__body" id={`bl-pub-coach-body-${tip.template}`}>
        {tip.body}
      </p>
      <div className="bl-pub-coach__actions">
        <button type="button" className="bl-pub-coach__got-it" onClick={dismiss}>
          Got it
        </button>
      </div>
    </div>
  );
}
